/**
 * Boot-time silent APK-update check for the Android app.
 *
 * With the Cache-First bundle strategy (v1.4.20), the WebView loads its
 * UI from bundled assets — no more remote fetch on cold start. That's a
 * big perf win, but the flip-side is that UI updates now ship inside a
 * new APK, not via a stealth server bundle swap.
 *
 * To keep users on the latest UI without them digging through Settings,
 * we run one silent update check ~5s after boot. If the admin panel
 * says a newer APK is out, we surface a toast with a one-tap install.
 *
 * Safe on browsers / iOS: `canInstallApkInApp()` returns false there,
 * so the check exits before doing anything.
 */
import { toast } from "sonner";

import { canInstallApkInApp } from "./apkUpdater";
import { checkForUpdates } from "./playerApi";

const SESSION_FLAG = "zapp_boot_update_checked_v1";
const DELAY_MS = 5000;

/**
 * Fires the check exactly once per app session. Idempotent + safe to
 * call from every mount of the outer route component.
 *
 * Returns a cleanup function so callers using it from a `useEffect`
 * can cancel the deferred timer during teardown.
 */
export function scheduleBootUpdateCheck(navigate) {
  if (!canInstallApkInApp()) return () => {};
  try {
    if (sessionStorage.getItem(SESSION_FLAG) === "1") return () => {};
    sessionStorage.setItem(SESSION_FLAG, "1");
  } catch {
    /* private-mode / disabled storage — just proceed */
  }

  const timer = setTimeout(async () => {
    try {
      const info = await checkForUpdates();
      if (!info?.has_update || !info?.download_url) return;

      toast(`Update available — v${info.latest_version || "?"}`, {
        description: info.changelog
          ? String(info.changelog).slice(0, 120)
          : "Tap Install to get the latest version.",
        duration: 12000,
        action: {
          label: "Install",
          onClick: () => navigate?.("/player/settings?updates=1"),
        },
      });
    } catch {
      /* offline / server down / auth issue — silently skip, retry next
         boot. Never block the app on this. */
    }
  }, DELAY_MS);

  return () => clearTimeout(timer);
}
